import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { CANADIAN_CITIES, COMMUNITY_CATEGORIES } from '../lib/constants';
import { FIELD_INPUT, FIELD_LABEL, ImageUploader, SubmitButton, PremiumGate } from '../components/submitHelpers';

export default function SubmitCommunity() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activityInput, setActivityInput] = useState('');
  const [form, setForm] = useState({
    name: '',
    category: '',
    city: '',
    description: '',
    images: [],
    website_url: '',
    instagram_url: '',
    linktree_url: '',
    phone: '',
    activities: [],
    meeting_frequency: '',
  });

  function update(k, v) { setForm({ ...form, [k]: v }); }

  function addActivity() {
    const v = activityInput.trim();
    if (!v) return;
    if (form.activities.includes(v)) return setActivityInput('');
    update('activities', [...form.activities, v]);
    setActivityInput('');
  }

  function removeActivity(a) {
    update('activities', form.activities.filter((x) => x !== a));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Community name is required');
    if (!form.category) return toast.error('Select a category');
    if (!form.city) return toast.error('Select a city');
    if (form.description.trim().length < 30) return toast.error('Description must be at least 30 characters');

    setLoading(true);
    try {
      await api('/submissions/community', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          name: form.name.trim(),
          description: form.description.trim(),
          website_url: form.website_url.trim() || null,
          instagram_url: form.instagram_url.trim() || null,
          linktree_url: form.linktree_url.trim() || null,
          phone: form.phone.trim() || null,
          meeting_frequency: form.meeting_frequency.trim() || null,
        }),
      });
      toast.success('Submitted for review!');
      navigate('/app/my-submissions');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  if (!user?.is_premium) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <BackBar to="/app/submit" />
        <PremiumGate message="Submitting a community is a Premium-only feature." />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="submit-community-page">
      <BackBar to="/app/submit" />
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        Submit a Community
      </h1>
      <p className="text-sm text-[#6D6875] mb-6">We'll review and publish within 48 hours.</p>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="submit-community-form">
        <div>
          <label className={FIELD_LABEL}>Community name *</label>
          <input data-testid="community-name-input" className={FIELD_INPUT} value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g. Toronto Yoruba Society" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={FIELD_LABEL}>Category *</label>
            <select data-testid="community-category-select" className={FIELD_INPUT} value={form.category} onChange={(e) => update('category', e.target.value)}>
              <option value="">Choose...</option>
              {COMMUNITY_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={FIELD_LABEL}>City *</label>
            <select data-testid="community-city-select" className={FIELD_INPUT} value={form.city} onChange={(e) => update('city', e.target.value)}>
              <option value="">Choose...</option>
              {CANADIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={FIELD_LABEL}>Description * <span className="text-xs font-normal text-[#B5B5B5]">(30+ characters)</span></label>
          <textarea data-testid="community-description-input" rows={4} maxLength={1500} className={FIELD_INPUT} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="What's the community about? Who should join? What activities do you do?" />
          <div className="text-xs text-right text-[#B5B5B5] mt-1">{form.description.length}/1500</div>
        </div>

        <div>
          <label className={FIELD_LABEL}>Activities (add up to 10)</label>
          <div className="flex gap-2 mb-2">
            <input
              data-testid="community-activity-input"
              className={FIELD_INPUT + ' flex-1'}
              value={activityInput}
              onChange={(e) => setActivityInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addActivity(); } }}
              placeholder="e.g. Game Nights"
            />
            <button
              type="button"
              onClick={addActivity}
              data-testid="community-add-activity-button"
              disabled={form.activities.length >= 10}
              className="px-4 py-2 bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-40 text-white rounded-lg flex items-center justify-center"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.activities.map((a) => (
              <span key={a} data-testid={`community-activity-chip-${a.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="inline-flex items-center gap-1 px-3 py-1 bg-[#FFE5DD] text-[#C8553D] rounded-full text-xs font-semibold">
                {a}
                <button type="button" onClick={() => removeActivity(a)} aria-label="remove" className="hover:bg-white/30 rounded-full">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className={FIELD_LABEL}>Meeting frequency</label>
          <input data-testid="community-frequency-input" className={FIELD_INPUT} value={form.meeting_frequency} onChange={(e) => update('meeting_frequency', e.target.value)} placeholder="Weekly, monthly, ad-hoc..." />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={FIELD_LABEL}>Website</label>
            <input data-testid="community-website-input" type="url" className={FIELD_INPUT} value={form.website_url} onChange={(e) => update('website_url', e.target.value)} placeholder="https://" />
          </div>
          <div>
            <label className={FIELD_LABEL}>Instagram</label>
            <input data-testid="community-instagram-input" type="url" className={FIELD_INPUT} value={form.instagram_url} onChange={(e) => update('instagram_url', e.target.value)} placeholder="https://instagram.com/..." />
          </div>
        </div>

        <ImageUploader images={form.images} setImages={(images) => update('images', images)} max={3} testIdPrefix="community-submission" />

        <SubmitButton loading={loading} testId="community-submit-button">
          <Send className="w-5 h-5" />
          Submit for Review
        </SubmitButton>
      </form>
    </div>
  );
}

function BackBar({ to }) {
  return (
    <Link to={to} data-testid="submit-back-button" className="inline-flex items-center gap-2 text-sm font-semibold text-[#6D6875] hover:text-[#C8553D] mb-4">
      <ArrowLeft className="w-4 h-4" />
      Back
    </Link>
  );
}
